import { Heart, Palette, UtensilsCrossed } from "lucide-react";
import { Separator } from "@/components/ui/separator";

const IMAGES = {
  exterior: "https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  breakfast: "https://images.unsplash.com/photo-1674042385136-28d23ca038ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHx0cm9waWNhbCUyMGJlZCUyMGJyZWFrZmFzdCUyMGNvenklMjByb29tfGVufDB8fHx8MTc3MjEyNTQzM3ww&ixlib=rb-4.1.0&q=85",
  room: "https://images.unsplash.com/photo-1673040158218-51d901c1c11d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHx0cm9waWNhbCUyMGJlZCUyMGJyZWFrZmFzdCUyMGNvenklMjByb29tfGVufDB8fHx8MTc3MjEyNTQzM3ww&ixlib=rb-4.1.0&q=85",
};

const values = [
  {
    icon: Heart,
    title: "HOSPITALITY WITH EDGE",
    desc: "We believe great hospitality doesn't have to be stuffy. Our approach is warm, genuine, and a little unconventional.",
  },
  {
    icon: Palette,
    title: "TECH IN EVERY DETAIL",
    desc: "From smart lighting to seamless connectivity, every piece of tech in our space has been chosen to elevate your stay.",
  },
  {
    icon: UtensilsCrossed,
    title: "FOOD IS LOVE",
    desc: "Our breakfast isn't just included — it's the highlight. Locally sourced ingredients, creative recipes, and always made with care.",
  },
];

const timeline = [
  { year: "2018", event: "Found the perfect beachfront property on Ocean Avenue" },
  { year: "2019", event: "Gutted. Rebuilt. Wired every surface with smart tech." },
  { year: "2020", event: "Soft launch to brave souls. They loved it." },
  { year: "2021", event: "Expanded to 4 unique rooms, each with its own personality" },
  { year: "2023", event: "Named 'Most Innovative B&B in Florida' by coastal magazine" },
  { year: "NOW", event: "Still upgrading. Still making breakfast. Still in love with this." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative bg-ink py-20">
        <div className="noise-overlay absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <p className="font-marker text-teal text-sm">The Back Story</p>
          <h1 className="mt-2 font-anton text-4xl tracking-wider text-sand stencil-text sm:text-6xl">
            ABOUT US
          </h1>
          <div className="mt-2 h-1 w-20 bg-coral" />
        </div>
      </section>

      {/* Story */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 className="font-anton text-2xl tracking-wider text-ink stencil-text sm:text-3xl">
                WE DIDN'T PLAN THIS.
                <br />
                <span className="text-coral">WE ENGINEERED IT.</span>
              </h2>
              <p className="mt-6 font-courier text-base leading-relaxed text-ink/80">
                It started with a crumbling beachfront house and a crazy idea: what if a bed
                and breakfast could feel like stepping into the future? What if every corner had
                something unexpected? What if the breakfast was actually good?
              </p>
              <p className="mt-4 font-courier text-base leading-relaxed text-ink/80">
                We're a small team with big ideas about what hospitality means. We don't do
                cookie-cutter rooms or continental breakfasts from a box. Every detail at Seas
                The Bay is intentional, from the smart home tech to the hand-picked coffee
                beans.
              </p>
              <p className="mt-4 font-courier text-base leading-relaxed text-ink/80">
                Located in the heart of Daytona Beach, we're close to everything but far enough
                from the noise. Our guests come for the beach and stay for the vibes. Or maybe
                they stay for the French toast. We don't judge.
              </p>
            </div>

            <div className="space-y-6">
              <div className="polaroid rotate-1">
                <img
                  src={IMAGES.exterior}
                  alt="Seas The Bay exterior"
                  className="aspect-[4/3] w-full object-cover"
                />
                <p className="mt-2 font-marker text-ink text-xs text-center">
                  Where the magic happens
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="font-anton text-2xl tracking-wider text-ink stencil-text sm:text-3xl">
            WHAT WE'RE ABOUT
          </h2>
          <div className="mt-2 h-1 w-20 bg-teal" />

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {values.map((value, i) => (
              <div key={i} className="card-brutal bg-sand p-8">
                <value.icon className="h-8 w-8 text-coral" />
                <h3 className="mt-4 font-anton text-lg tracking-wider text-ink">
                  {value.title}
                </h3>
                <p className="mt-3 font-courier text-sm leading-relaxed text-ink/70">
                  {value.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="font-anton text-2xl tracking-wider text-ink stencil-text sm:text-3xl">
            THE TIMELINE
          </h2>
          <div className="mt-2 h-1 w-20 bg-coral" />

          <div className="mt-12 space-y-0">
            {timeline.map((item, i) => (
              <div key={i} className="flex gap-6 border-l-2 border-ink pl-6 pb-8 last:pb-0">
                <div className="relative">
                  <div className="absolute -left-[31px] top-0 h-4 w-4 border-2 border-ink bg-teal" />
                </div>
                <div>
                  <span className="font-anton text-lg tracking-wider text-coral">
                    {item.year}
                  </span>
                  <p className="mt-1 font-courier text-sm text-ink/80">
                    {item.event}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photos */}
      <section className="bg-ink py-20">
        <div className="noise-overlay absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="grid gap-8 md:grid-cols-2">
            <div className="polaroid -rotate-1">
              <img
                src={IMAGES.breakfast}
                alt="Breakfast at Seas The Bay"
                className="aspect-[4/3] w-full object-cover"
              />
              <p className="mt-2 font-marker text-ink text-xs text-center">
                Breakfast game: strong
              </p>
            </div>
            <div className="polaroid rotate-2">
              <img
                src={IMAGES.room}
                alt="Room interior"
                className="aspect-[4/3] w-full object-cover"
              />
              <p className="mt-2 font-marker text-ink text-xs text-center">
                Your home away from home
              </p>
            </div>
          </div>

          <Separator className="my-12 bg-sand/20" />

          <div className="text-center">
            <p className="font-marker text-teal text-lg">
              "The best things in life are the people you love, the places you've been,
              and the memories you've made along the way."
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
